/**
 * "At-Risk / Inactive Players" retention card.
 *
 * Renders the `dormantPlayers` dataset (players enrolled in an active league
 * with no reported result in the dormancy window) as an actionable table:
 * Player · League · Days Since Last Match · Nudge.
 *
 * The "Nudge" action is a PLACEHOLDER — there is no notifications endpoint yet,
 * so `wireNudgeButtons` only gives inline acknowledgement. Wire it after the
 * card is injected into the DOM (it is idempotent and safe to call repeatedly).
 *
 * Styling follows the two-skin model: night markup is base + `dark:`, and
 * `daylight:` classes are APPENDED for the light skin (never edited in).
 */

import type { DormantPlayer } from "shared";
import { t } from "../i18n/config";

const esc = (value: string): string => {
  const div = document.createElement("div");
  div.textContent = value ?? "";
  return div.innerHTML;
};

/** Severity styling for the inactivity badge — redder the longer the silence. */
function daysCell(days: number | null): string {
  if (days === null) {
    return `<span class="badge bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 daylight:bg-day-sunken daylight:text-day-ink-soft">${t("insights.dormant.noMatches")}</span>`;
  }
  const tone =
    days >= 60
      ? "bg-error/10 text-error daylight:bg-day-danger/10 daylight:text-day-danger"
      : "bg-warning/10 text-warning daylight:bg-day-warning/10 daylight:text-day-warning";
  const label = t(days === 1 ? "insights.dormant.days_one" : "insights.dormant.days_other", { count: days });
  return `<span class="badge ${tone}">${label}</span>`;
}

function nudgeButton(p: DormantPlayer): string {
  return `
    <button
      type="button"
      data-nudge-player-id="${p.playerId}"
      data-nudge-league-id="${p.leagueId}"
      data-nudge-name="${esc(p.playerName)}"
      class="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1 border border-slate-300 dark:border-slate-600 daylight:border-day-ink/20"
      title="${t("insights.dormant.nudgeTitle")}">
      <span class="material-symbols-outlined text-sm">notifications_active</span>
      <span>${t("insights.dormant.nudge")}</span>
    </button>`;
}

function emptyState(): string {
  return `
    <div class="text-center py-8">
      <span class="material-symbols-outlined text-4xl text-success daylight:text-day-success flex justify-center mb-2">sentiment_satisfied</span>
      <h4 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.dormant.emptyTitle")}</h4>
      <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-sm mx-auto mt-1">${t("insights.dormant.emptyBody")}</p>
    </div>
  `;
}

/**
 * Render the retention card. `showLeagueColumn` defaults to true (organizer /
 * platform context); pass false on a single-league view where the column is
 * redundant.
 */
export function renderDormantPlayersCard(
  players: DormantPlayer[],
  options: { showLeagueColumn?: boolean } = {},
): string {
  const showLeague = options.showLeagueColumn ?? true;

  const heading = `
    <div class="flex items-center gap-2 mb-4">
      <span class="material-symbols-outlined text-warning daylight:text-day-warning">person_alert</span>
      <h3 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.dormant.title")}</h3>
      ${players.length > 0 ? `<span class="badge badge-warning ml-1">${players.length}</span>` : ""}
    </div>
  `;

  if (players.length === 0) {
    return `<section class="card">${heading}${emptyState()}</section>`;
  }

  const headerCell = "px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted";

  const rows = players
    .map(
      (p) => `
      <tr class="border-t border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
        <td class="px-3 py-3 font-medium text-slate-900 dark:text-white daylight:text-day-ink">${esc(p.playerName)}</td>
        ${showLeague ? `<td class="px-3 py-3 text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${esc(p.leagueName)}</td>` : ""}
        <td class="px-3 py-3">${daysCell(p.daysSinceLastMatch)}</td>
        <td class="px-3 py-3 text-right">${nudgeButton(p)}</td>
      </tr>`,
    )
    .join("");

  return `
    <section class="card">
      ${heading}
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr>
              <th class="${headerCell}">${t("insights.dormant.colPlayer")}</th>
              ${showLeague ? `<th class="${headerCell}">${t("insights.dormant.colLeague")}</th>` : ""}
              <th class="${headerCell}">${t("insights.dormant.colDays")}</th>
              <th class="${headerCell} text-right">${t("insights.dormant.colAction")}</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>
  `;
}

/**
 * Wire the placeholder "Nudge" buttons inside `root`. Idempotent — already-wired
 * buttons are skipped — so it is safe to call after every re-render. Until a
 * real notifications endpoint exists, clicking only acknowledges inline.
 */
export function wireNudgeButtons(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>("[data-nudge-player-id]").forEach((btn) => {
    if (btn.dataset.nudgeWired === "1") return;
    btn.dataset.nudgeWired = "1";
    btn.addEventListener("click", () => {
      btn.disabled = true;
      btn.classList.add("opacity-60", "cursor-default");
      btn.innerHTML = `<span class="material-symbols-outlined text-sm">check</span><span>${t("insights.dormant.nudged")}</span>`;
    });
  });
}
